import { useLocalStorageState } from 'ahooks';
import { Form, FormInstance } from 'antd';
import { isEmpty } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useAppConfig } from '@/config/context';
import { getProductId, isAndroid } from '@/infrastructure/product-info';
import { getCompareDateValue, searchValueDeserializer } from '@/infrastructure/utils/issue';
import { toArray } from '@/infrastructure/utils/to-array';
import {
  FormEntry,
  FormType, IFormRef,
  QueryForm,
} from '@/presentation/components/form';
import { DEFAULT_MONITOR_LIMIT_DAYS } from '@/presentation/components/form/components/time-range';
import { VersionSelectGroupName } from '@/presentation/components/form/components/version-select-group';
import { NUMBER_MATCH_OPTIONS } from '@/presentation/components/form/standards/types';
import { FilterEntry, FormColumnGroup } from '@/presentation/components/form/types';
import {
  getVersionLabel,
  topNumLabel,
  TOP_RULES,
  onChangeAppVersion,
  getShowSwitchStatus,
} from '@/presentation/components/form/utils';
import { ABTEST_TIPS, BUNDLE_TIPS_CASE, hintText } from '@/presentation/components/hint-text/hintText';
import { enumFilterTips, numberFilterTips, versionFilterTips } from '@/presentation/components/issue-list/components/tips';
import { SEARCH_VALUES } from '@/presentation/config/storage';
import { SearchValueStorageType } from '@/presentation/config/type';
import {
  AppVersionOperator,
  FIELD_ALL,
  FIELD_LABEL,
  FieldKey,
  Zone,
} from '@/proto/field';
import { IssueType } from '@/proto/issue';
import { TimeType } from '@/proto/metric';
import { FieldList } from '@/proto/token';

interface IProps {
  onFinish: (value: Record<string, any>) => void;
  entry: FilterEntry;
  initialValue?: FieldList & { timeRange: string[], isValueInit?: boolean };
  onValueInited?: (value: Record<string, any>) => void;
  form: FormInstance;
  tokenInitValues: FieldList & { isValueInit?: boolean };
}

/**
 * 大图分析-问题列表>查询表单
 */

export const ProblemForm: FC<IProps> = observer((props) => {
  const { tokenInitValues } = props;
  const [topForm] = Form.useForm();
  const tableRef = useRef<IFormRef>(null);
  const form = props.form ? props.form : topForm;
  const [showSwitch, setShowSwitch] = useState(false);
  const [searchValues, setSearchValues] = useLocalStorageState<SearchValueStorageType>(`${SEARCH_VALUES}-${props.entry}-${getProductId()}`, {
    defaultValue: [],
  });
  const config = useAppConfig();

  useEffect(() => {
    if (!isEmpty(tokenInitValues)) {
      const { isValueInit = true, appVersionGroup, appVersionOperator } = tokenInitValues;
      // 链接有token时，强制展示有value值的表单项
      tableRef?.current?.showFormItem?.(entries, tokenInitValues)?.then(() => {
        form.setFieldsValue(tokenInitValues);
        if (isValueInit) {
          props.onValueInited?.(form.getFieldsValue());
        }
        // 初始化显示 【只显示新增问题】字段
        setShowSwitch(getShowSwitchStatus({
          values: toArray(appVersionGroup ?? ''),
          operator: appVersionOperator ?? '',
        }));
      });
    }
  }, [tokenInitValues]);

  useEffect(() => {
    if (!isEmpty(props.initialValue)) {
      const { isValueInit = true, appVersionGroup, appVersionOperator } = props.initialValue;
      form.setFieldsValue(props.initialValue);
      if (isValueInit) {
        props.onValueInited?.(form.getFieldsValue());
      }
      // 初始化显示 【只显示新增问题】字段
      setShowSwitch(getShowSwitchStatus({
        values: toArray(appVersionGroup ?? ''),
        operator: appVersionOperator ?? '',
      }));
    }
  }, [props.initialValue]);

  const entries: FormEntry[] = [
    {
      type: FormType.TimeRangeCascade,
      name: 'timeRange',
      labelString: FIELD_LABEL[FieldKey.TimeRange],
      scope: 'issues',
      label: FIELD_LABEL[FieldKey.TimeRange],
      deleteable: false,
      limitDays: DEFAULT_MONITOR_LIMIT_DAYS,
      showTime: props.entry === FilterEntry.MemoryBigPcDetail || props.entry === FilterEntry.MemoryBigPcList,
      initTimeWindow: TimeType.MINUTE5,
      hiddenTimeWindow: props.entry !== FilterEntry.MemoryBigPcList,
      isIssuesTimeWindow: props.entry === FilterEntry.MemoryBigPcList,
    },
    {
      type: FormType.VersionSelectGroup,
      leftName: FieldKey.AppVersionOperator,
      rightName: FieldKey.AppVersion,
      label: FIELD_LABEL[FieldKey.AppVersionOperator],
      leftFieldKey: FieldKey.AppVersionOperator,
      rightFieldKey: FieldKey.AppVersion,
      fieldScope: Zone.BigPic,
      scope: 'issues',
      mode: 'tags',
      leftInitialValue: AppVersionOperator.Or,
      rightInitialValue: [FIELD_ALL],
      name: VersionSelectGroupName.appVersionGroup,
      deleteable: false,
      hasMaxWidth: true,
    },
    {
      type: FormType.Select,
      name: 'appVersionMode',
      label: hintText(FIELD_LABEL[FieldKey.AppVersionMode], versionFilterTips),
      labelString: FIELD_LABEL[FieldKey.AppVersionMode],
      fieldKey: FieldKey.AppVersionMode,
      fieldScope: Zone.BigPic,
      mode: 'multiple',
      initialValue: [FIELD_ALL],
      group: FormColumnGroup.ApplicationInfo,
    },
    {
      type: FormType.Select,
      name: 'scene',
      label: hintText(FIELD_LABEL[FieldKey.Scene], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.Scene],
      fieldKey: FieldKey.Scene,
      fieldScope: Zone.BigPic,
      mode: 'single',
      initialValue: FIELD_ALL,
      dropdownMatchSelectWidth: 500,
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.Input,
      name: 'brand',
      label: FIELD_LABEL[FieldKey.Brand],
      disabled: !isAndroid(),
      group: FormColumnGroup.DeviceInfo,
    },
    {
      type: FormType.Select,
      name: 'processName',
      label: hintText(FIELD_LABEL[FieldKey.ProcessName], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.ProcessName],
      fieldKey: FieldKey.ProcessName,
      fieldScope: Zone.BigPic,
      mode: 'single',
      initialValue: FIELD_ALL,
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.VersionSelect,
      name: FieldKey.OsVersion,
      versionName: FieldKey.OsVersion,
      label: getVersionLabel(FIELD_LABEL[FieldKey.OsVersion]),
      fieldKey: FieldKey.OsVersion,
      labelString: FIELD_LABEL[FieldKey.OsVersion],
      fieldScope: Zone.BigPic,
      group: FormColumnGroup.DeviceInfo,
    },
    {
      type: FormType.Select,
      name: 'sdkVersion',
      label: hintText(FIELD_LABEL[FieldKey.SdkVersion], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.SdkVersion],
      fieldKey: FieldKey.SdkVersion,
      fieldScope: Zone.BigPic,
      mode: 'single',
      initialValue: FIELD_ALL,
      dropdownMatchSelectWidth: 300,
      group: FormColumnGroup.ApplicationInfo,
    },
    {
      type: FormType.AutoComplete,
      name: 'model',
      label: FIELD_LABEL[FieldKey.Model],
      fieldKey: FieldKey.Model,
      fieldScope: Zone.BigPic,
      initialValue: FIELD_ALL,
      group: FormColumnGroup.DeviceInfo,
    },
    {
      type: FormType.Select,
      name: 'showType',
      label: hintText(FIELD_LABEL[FieldKey.ShowType], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.ShowType],
      fieldKey: FieldKey.ShowType,
      fieldScope: Zone.BigPic,
      mode: 'single',
      initialValue: FIELD_ALL,
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.Select,
      name: 'activityName',
      label: hintText(FIELD_LABEL[FieldKey.ActivityName], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.ActivityName],
      fieldKey: FieldKey.ActivityName,
      fieldScope: Zone.BigPic,
      mode: 'single',
      initialValue: FIELD_ALL,
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.Input,
      name: 'accountId',
      label: '用户ID',
      group: FormColumnGroup.UserInfo,
    },
    {
      type: FormType.Input,
      name: 'qimei',
      label: '设备ID',
      group: FormColumnGroup.UserInfo,
    },
    {
      type: FormType.Input,
      name: 'launchId',
      label: FIELD_LABEL[FieldKey.LaunchId],
      labelString: '启动ID',
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.Input,
      name: 'processLaunchId',
      label: FIELD_LABEL[FieldKey.ProcessLaunchId],
      labelString: '进程启动ID',
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.Input,
      name: 'clientIdentify',
      label: '消息ID',
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.Input,
      name: 'feature',
      label: 'Issue ID',
      disabled: props.entry !== FilterEntry.MemoryBigPcList,
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.Input,
      name: 'funcName',
      label: '问题特征',
      disabled: props.entry !== FilterEntry.MemoryBigPcList,
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.Select,
      name: 'bundleId',
      label: hintText(FIELD_LABEL[FieldKey.BundleId], BUNDLE_TIPS_CASE),
      labelString: FIELD_LABEL[FieldKey.BundleId],
      fieldKey: FieldKey.BundleId,
      fieldScope: Zone.BigPic,
      mode: 'single',
      initialValue: FIELD_ALL,
      group: FormColumnGroup.DeviceInfo,
    },
    {
      type: FormType.Select,
      name: FieldKey.ShiplyTag,
      label: hintText(FIELD_LABEL[FieldKey.ShiplyTag], ABTEST_TIPS),
      labelString: FIELD_LABEL[FieldKey.ShiplyTag],
      initialValue: FIELD_ALL,
      fieldKey: FieldKey.TestTag,
      fieldScope: Zone.BigPic,
      mode: 'tags',
      disabled: !config.enableAbTestTag,
      group: FormColumnGroup.Custom,
    },
    {
      type: FormType.Select,
      name: FieldKey.AbTestTag,
      label: hintText(FIELD_LABEL[FieldKey.AbTestTag], ABTEST_TIPS),
      labelString: FIELD_LABEL[FieldKey.AbTestTag],
      initialValue: FIELD_ALL,
      fieldKey: FieldKey.AbTestTagLine,
      fieldScope: Zone.BigPic,
      mode: 'tags',
      group: FormColumnGroup.Custom,
    },
    {
      type: FormType.Select,
      name: FieldKey.TagsInFilter,
      label: FIELD_LABEL[FieldKey.TagsInFilter],
      initialValue: [FIELD_ALL],
      fieldScope: Zone.BigPic,
      fieldKey: FieldKey.TagsInFilterUnderline,
      mode: 'tags',
      compareDisabled: true,
      group: FormColumnGroup.Tag,
    },
    // {
    //   type: FormType.Input,
    //   name: FieldKey.AbTestTag,
    //   label: '实验标签',
    //   initialValue: FIELD_ALL,
    // },
    {
      type: FormType.Input,
      name: 'topNum',
      label: topNumLabel,
      rules: TOP_RULES,
      disabled: props.entry !== FilterEntry.MemoryBigPcList,
      labelString: 'Top问题数',
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.Input,
      name: 'view_tag',
      label: '图片信息',
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.Select,
      name: 'romName',
      label: hintText(FIELD_LABEL[FieldKey.Rom], ABTEST_TIPS),
      labelString: FIELD_LABEL[FieldKey.Rom],
      initialValue: FIELD_ALL,
      fieldKey: FieldKey.Rom,
      fieldScope: Zone.BigPic,
      mode: 'tags',
      group: FormColumnGroup.DeviceInfo,
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.StandardLaunchDuration,
      label: hintText('使用时长（秒）', numberFilterTips),
      labelString: '使用时长（秒）',
      options: NUMBER_MATCH_OPTIONS,
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.StandardPicOverSize,
      label: hintText('图片超标大小（KB）', numberFilterTips),
      labelString: '图片超标大小（KB）',
      options: NUMBER_MATCH_OPTIONS,
      group: FormColumnGroup.ContextInfo,
      compareDisabled: true,
      initialDeleted: false,
    },
    {
      type: FormType.TdsArea,
      name: FieldKey.StandardArea,
      label: '地域',
      group: FormColumnGroup.UserInfo,
      labelString: '地域',
      fieldScope: Zone.BigPic,
      compareDisabled: true,
      initialDeleted: true,
    },
    {
      type: FormType.CustomFields,
      name: FieldKey.UserCustom,
      label: '自定义维度',
      group: FormColumnGroup.Custom,
    },
    {
      type: FormType.CustomKeys,
      name: FieldKey.BizExtendInfo,
      label: '自定义字段',
      group: FormColumnGroup.Custom,
    },
    {
      type: FormType.Select,
      name: 'showNew',
      label: hintText('只显示新增问题', enumFilterTips),
      labelString: '只显示新增问题',
      disabled: !showSwitch || props.entry !== FilterEntry.MemoryBigPcList,
      deleteable: false,
      mode: 'single',
      fieldKey: FieldKey.ShowNew,
      fieldScope: Zone.BigPic,
      initialValue: false,
      compareDisabled: true,
    },
  ];

  const onFinish = (value: Record<string, any>, compareSwitch?: boolean) => {
    const compareDateValue = getCompareDateValue(value, compareSwitch);
    if (compareSwitch) {
      form.setFieldsValue(compareDateValue);
    }
    props.onFinish({ ...value, ...compareDateValue });
  };

  return <QueryForm
    form={form}
    cachePrefix={'prefix4'}
    ref={tableRef}
    entries={entries}
    onFinish={onFinish}
    onChange={async (__, fields) => onChangeAppVersion({
      values: toArray(fields.appVersionGroup ?? ''),
      operator: fields?.appVersionOperator ?? '',
    }, setShowSwitch)}
    onReset={() => setShowSwitch(false)}
    pageEntry={props.entry}
    issueType={IssueType.ISSUE_TYPE_BITMAP}
    hasCompare={props.entry === FilterEntry.MemoryBigPcList}
    defaultValues={props.initialValue}
    isSaveSearchValues={true}
    searchValues={searchValueDeserializer(searchValues)}
    setSearchValues={setSearchValues}
    renderByGroup
  />;
});
